import { GetOrderForAdminQuery, Roles_Enum } from "./graphql-generated";

export interface JwtData {
  accountId: string;
  email: string;
  displayName: string;
  role: Roles_Enum;
}

export type PostType = "review" | "list" | "article";
export interface IPostType {
  name: PostType;
  color: string;
}

export const postTypes: IPostType[] = [
  { name: "review", color: "yellow-default" },
  { name: "list", color: "red-default" },
  { name: "article", color: "green-default" },
];

export interface UserError {
  code: number;
  removeCredentials?: boolean;
  message:
    | "Refresh token is invalid"
    | "No credentials provided"
    | "You are not authorised to view this page.";
}

export type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

export type EmailOrderItem = {
  label: string;
  quantity: number;
  unit_price_string: string;
  unit_price: number;
};

export interface EmailOrderConfirmation {
  order_number: number;
  order_items: EmailOrderItem[];
  order_total?: string;
}
export interface EmailOrderFulfilled extends EmailOrderConfirmation {
  is_pickup: "true" | "false";
}

export interface EmailOrderPartiallyFulfilled extends EmailOrderFulfilled {}

export interface EmailVoucher {
  voucher_value: string;
  voucher_code: string;
  message: string;
}

interface EventBase {
  image_link: string;
  event_name: string;
  event_date: string;
  event_location: string;
  event_book: string;
  book_url: string;
}
export interface EmailEventVoucher extends EventBase {
  voucher_value: string;
  voucher_code: string;
}
export interface EmailBookclub extends EventBase {}

export interface EmailEventVoucherOnline extends EventBase, EmailEventVoucher {
  event_link: string;
}

export interface EmailEventBook extends EventBase {}

export interface EmailEventOnline extends EventBase {
  event_link: string;
}

export interface EmailEventFree extends EventBase {}

export interface EmailEventOnlineReminder extends EmailEventOnline {}
export interface EmailEventInPersonReminder extends EventBase {}

export interface EmailSubscriptionGifted {
  manage_code: string;
  manage_link: string;
  subscription_title: string;
  credits: string;
}

export interface EmailSubscriptionSubscribed {
  manage_code: string;
  manage_link: string;
  subscription_title: string;
}

export type EmailType =
  | "Reminder: Online Event"
  | "Reminder: In Person Event"
  | "Confirmation: Online Event"
  | "Confirmation: Online Event & Voucher"
  | "Confirmation: In Person Event with Book"
  | "Confirmation: In Person Event with Voucher"
  | "Confirmation: In Person Event free"
  | "Subscription: Subscribed"
  | "Subscription: Gifted"
  | "Confirmation: Bookclub"
  | "Voucher"
  | "Voucher Transfer"
  | "Order Confirmation"
  | "Order Ready"
  | "Order Partially Ready"
  | "Welcome"
  | "Password Reset";

export type EmailVariables<T> = T extends "Reminder: Online Event"
  ? EmailEventOnlineReminder
  : T extends "Reminder: In Person Event"
  ? EmailEventInPersonReminder
  : T extends "Confirmation: Online Event"
  ? EmailEventOnline
  : T extends "Confirmation: Online Event & Voucher"
  ? EmailEventVoucherOnline
  : T extends "Confirmation: In Person Event with Book"
  ? EmailEventBook
  : T extends "Confirmation: In Person Event with Voucher"
  ? EmailEventVoucher
  : T extends "Confirmation: Bookclub"
  ? EmailBookclub
  : T extends "Confirmation: In Person Event free"
  ? EmailEventFree
  : T extends "Voucher" | "Voucher Transfer"
  ? EmailVoucher
  : T extends "Order Confirmation"
  ? EmailOrderConfirmation
  : T extends "Order Ready"
  ? EmailOrderFulfilled
  : T extends "Order Partially Ready"
  ? EmailOrderPartiallyFulfilled
  : T extends "Welcome"
  ? null
  : T extends "Password Reset"
  ? { password: string }
  : T extends "Subscription: Subscribed"
  ? EmailSubscriptionSubscribed
  : T extends "Subscription: Gifted"
  ? EmailSubscriptionGifted
  : null;

export interface EmailMessage<T extends EmailType> {
  From: {
    Email: string;
    Name: string;
  };
  To: { Email: string; Name: string }[];
  TemplateLanguage: true;
  TemplateID: number;
  Subject: string;
  Variables: EmailVariables<T>;
}

export interface PartiallyFulfillOrderEmailParams {
  books: GetOrderForAdminQuery["orders_by_pk"]["order_books"];
  products: GetOrderForAdminQuery["orders_by_pk"]["order_products"];
  orderId: number;
  address: GetOrderForAdminQuery["orders_by_pk"]["address"];
  shippingOption: GetOrderForAdminQuery["orders_by_pk"]["shipping_option"];
}

export type FulfillOrderEmailParams = GetOrderForAdminQuery["orders_by_pk"];

export type ConfirmEventTicketParams = {
  is_free_attendee?: boolean;
  is_online_viewer?: boolean;
  order_book_id?: string;
  event: {
    start_time: string;
    location?: string;
    title: string;
    image?: string;
    slug: string;
    event_books?: {
      book: {
        isbn: number;
        author?: string;
        title: string;
      };
    }[];
  };
  voucher?: {
    id: string;
    initial_amount: number;
  };
  address: {
    contact_email: string;
    name: string;
  };
};

export type EmailVoucherParams = {
  recipient: string;
  id: string;
  initial_amount: number;
  message: string;
};

export type EmailSubscriptionParams = {
  recipient: string;
};

export type EmailSubscriptionGiftedParams = EmailSubscriptionGifted &
  EmailSubscriptionParams;
export type EmailSubscriptionSubscribedParams = EmailSubscriptionGifted &
  EmailSubscriptionParams & { name: string };

export interface IEmailWebhook<T extends EmailType> {
  type: T;
  data: T extends "Order Partially Ready"
    ? PartiallyFulfillOrderEmailParams
    : T extends "Order Ready"
    ? FulfillOrderEmailParams
    : T extends "Confirmation: Online Event"
    ? ConfirmEventTicketParams
    : T extends "Confirmation: In Person Event free"
    ? ConfirmEventTicketParams
    : T extends "Voucher"
    ? EmailVoucherParams
    : T extends "Subscription: Subscribed"
    ? EmailSubscriptionSubscribedParams
    : T extends "Subscription: Gifted"
    ? EmailSubscriptionGiftedParams
    : null;
}
